import { Eye, FileCheck2, Mail } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DOCUMENT_TYPE_BG } from '@/i18n';
import { formatDateTime } from '@/lib/format';
import { api } from '@/api/client';
import { useDocumentsOf, useIssueInvoice } from '@/api/hooks/documents';
import { useExportLog, useExportSettings, useSendOrderDocuments } from '@/api/hooks/exports';
import type { ExportKind, ExportLogRow, ExportResult, OrderDocSet } from '@/api/types';
import { EXPORT_KIND_LABEL, ExportStatusBadge } from '@/components/documents/DocumentEmailPanel';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { DOC_SLOTS, DocSlotRow, UploadButton } from '@/components/documents/DocSet';
import { toast } from '@/stores/toast';

/**
 * Трите документа на заявката — заявка, ЧМР, фактура — и всички файлове към
 * нея. Липсващ или върнат документ се качва оттук; фактурата може и да се
 * издаде от системата.
 */
export function OrderDocumentsCard({
  orderId,
  docs,
  canIssue,
}: {
  orderId: string;
  docs: OrderDocSet | undefined;
  canIssue: boolean;
}) {
  const files = useDocumentsOf({ orderId });
  const issue = useIssueInvoice(orderId);
  const items = files.data?.items ?? [];

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            <FileCheck2 className="size-4 text-ink-dim" />
            Документи на заявката
          </span>
        }
        subtitle={docs ? `${docs.accepted} от 3 приети · ${items.length} файла` : `${items.length} файла`}
        actions={<UploadButton type="other" target={{ orderId }} label="Друг файл" />}
      />
      <CardBody className="space-y-1">
        {docs ? (
          <div className="divide-y divide-line">
            {DOC_SLOTS.map((slot) => (
              <DocSlotRow
                key={slot.key}
                label={slot.label}
                slot={docs[slot.key]}
                type={slot.type}
                target={{ orderId }}
                extra={
                  slot.key === 'invoice' && canIssue && docs.invoice.state !== 'accepted' ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={issue.isPending}
                      onClick={() =>
                        issue.mutate(undefined, {
                          onSuccess: (inv) => toast.ok('Фактурата е издадена', inv.number ?? undefined),
                        })
                      }
                    >
                      Издай
                    </Button>
                  ) : null
                }
              />
            ))}
          </div>
        ) : null}

        <OrderEmailRow orderId={orderId} docs={docs} />

        {items.length > 0 ? (
          <ul className="mt-2 divide-y divide-line border-t border-line">
            {items.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
                <span className="font-medium">{DOCUMENT_TYPE_BG[d.type] ?? d.type}</span>
                <span className="min-w-0 flex-1 truncate text-xs text-ink-faint">{d.fileName}</span>
                <Badge tone={d.status === 'accepted' ? 'ok' : d.status === 'rejected' ? 'danger' : 'warn'}>
                  {d.status === 'accepted' ? 'приет' : d.status === 'rejected' ? 'върнат' : 'за преглед'}
                </Badge>
                {d.previewUrl ? (
                  <a
                    href={d.previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                  >
                    <Eye className="size-3.5" />
                    отвори
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="pt-1 text-xs text-ink-faint">Още няма качени файлове.</p>
        )}
      </CardBody>
    </Card>
  );
}

const KINDS: ExportKind[] = ['transport_order', 'cmr', 'invoice'];

const RESULT_TEXT: Record<ExportResult['status'], string> = {
  sent: 'изпратено',
  failed: 'неуспех',
  no_smtp: 'пощата не е настроена',
  no_recipients: 'няма адрес',
  missing: 'няма приет файл',
  already_sent: 'вече е изпратено',
  incomplete: 'чака трите документа',
};

/**
 * Трите документа по имейл: кое до къде е тръгнало и „Изпрати пак". Само
 * последният опит за всеки вид — пълният дневник е в „ЧМР и документи".
 */
function OrderEmailRow({ orderId, docs }: { orderId: string; docs: OrderDocSet | undefined }) {
  const settings = useExportSettings();
  const log = useExportLog(orderId);
  const send = useSendOrderDocuments(orderId);
  const latest = new Map<ExportKind, ExportLogRow>();
  for (const row of log.data ?? []) if (!latest.has(row.kind)) latest.set(row.kind, row);
  const anySent = [...latest.values()].some((row) => row.status === 'sent');
  const noAddresses = settings.data ? KINDS.every((k) => settings.data.recipients[k].length === 0) : false;

  return (
    <div className="mt-2 rounded-lg border border-line bg-raised/40 p-2">
      <div className="flex flex-wrap items-center gap-2">
        <Mail className="size-3.5 text-ink-dim" />
        <span className="text-xs font-medium">Имейл</span>
        {KINDS.map((kind) => {
          const row = latest.get(kind);
          return (
            <span key={kind} className="inline-flex items-center gap-1 text-[11px] text-ink-dim">
              {EXPORT_KIND_LABEL[kind]}:
              {row ? <ExportStatusBadge row={row} /> : <span className="text-ink-faint">не е пращано</span>}
            </span>
          );
        })}
        <Button
          size="sm"
          variant="secondary"
          className="ml-auto"
          loading={send.isPending}
          disabled={noAddresses}
          onClick={() =>
            send.mutate(undefined, {
              onSuccess: (results) => {
                const sent = results.filter((r) => r.status === 'sent').length;
                const summary = results.map((r) => `${EXPORT_KIND_LABEL[r.kind]}: ${RESULT_TEXT[r.status]}`).join(' · ');
                if (sent > 0) toast.ok(`Изпратени: ${sent} от ${results.length}`, summary);
                else toast.warn('Нищо не е изпратено', summary);
              },
              onError: (error) => toast.error('Изпращането не стана', error instanceof Error ? error.message : undefined),
            })
          }
        >
          {anySent ? 'Изпрати пак' : 'Изпрати по имейл'}
        </Button>
      </div>
      {noAddresses ? (
        <p className="mt-1 text-[11px] text-ink-faint">
          Адресите за документите не са въведени — „ЧМР и документи" → „Изпращане по имейл".
        </p>
      ) : docs && !docs.complete ? (
        <p className="mt-1 text-[11px] text-ink-faint">
          Тръгват сами, щом и трите са приети ({docs.accepted} от 3). Бутонът праща каквото има сега.
        </p>
      ) : null}
    </div>
  );
}

interface HistoryRow {
  id: string;
  fromLabel: string | null;
  toLabel: string;
  at: string;
  actorLabel: string;
  source: string;
  note: string | null;
}

/** Кой, кога и откъде е сменил състоянието на заявката. */
export function StatusHistoryCard({ orderId }: { orderId: string }) {
  const { data } = useQuery({
    queryKey: ['orders', 'history', orderId],
    queryFn: () => api.get<HistoryRow[]>(`/orders/${orderId}/status-history`),
  });
  const rows = data ?? [];
  return (
    <Card>
      <CardHeader title="История" />
      <CardBody>
        {rows.length === 0 ? (
          <p className="text-xs text-ink-dim">Няма записи.</p>
        ) : (
          <ul className="space-y-1.5">
            {rows.map((row) => (
              <li key={row.id} className="flex flex-wrap gap-x-3 text-xs">
                <span className="w-full shrink-0 font-mono text-ink-faint sm:w-32">{formatDateTime(row.at)}</span>
                <span className="font-medium text-ink">
                  {row.fromLabel ? `${row.fromLabel} → ` : ''}
                  {row.toLabel}
                </span>
                <span className="text-ink-dim">{row.note ?? ''}</span>
                <span className="ml-auto text-ink-faint">{row.actorLabel}</span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
